import { useEffect } from "react";
import { AccountType } from "./enums";

// What people type, for Microsoft Clarity. Clarity never records the content
// of input boxes, text areas or dropdowns, in any masking mode, and that can't
// be changed (https://learn.microsoft.com/clarity/setup-and-installation/clarity-masking):
// a replay of the checkout showed the email, the notes, etc. as dots. So when
// a field is left (the "change" event), its value goes to Clarity as a custom
// tag of the session ("More details" > Custom tags in a recording, and a
// filter), and an email also names the session (clarity "identify"), so the
// recordings list shows whose it is. Alejo chose to mask as little as possible
// (2026-09-16); passwords, card fields and the office are still left out.

const MAX = 255; // Clarity keeps tag values up to this length
const SKIP_TYPES = ["password", "hidden", "file", "checkbox", "radio", "submit", "button"];

const clarity = (...args) => {
  try {
    if (typeof window.clarity === "function") {
      window.clarity(...args);
    }
  } catch (e) {
    // Clarity is optional: never break the page for it
  }
};

const clean = (s) => String(s || "").replace(/\s+/g, " ").trim();

// A readable name for the field: its label, else its name/id/placeholder.
const fieldName = (el) => {
  const byFor = el.id && document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
  const label = (byFor || el.closest("label") || {}).textContent;
  return clean(el.getAttribute("aria-label") || label || el.name || el.id || el.placeholder || el.tagName.toLowerCase()).slice(0, 60);
};

const isEmail = (el, value) => el.type === "email" || /email/i.test(el.name || el.id || "") || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const onChange = (e) => {
  const el = e.target;
  if (!el || !["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) {
    return;
  }
  if (SKIP_TYPES.includes((el.type || "").toLowerCase()) || /^cc-/.test(el.autocomplete || "") || el.closest("[data-clarity-skip]")) {
    return;
  }
  if (/^\/office(\/|$)/.test(window.location.pathname)) {
    return;
  }
  const value = clean(el.tagName === "SELECT" ? (el.selectedOptions[0] || {}).text : el.value);
  if (!value) {
    return;
  }
  clarity("set", fieldName(el), value.slice(0, MAX));
  if (isEmail(el, value)) {
    clarity("identify", value.toLowerCase(), undefined, undefined, value.toLowerCase());
  }
};

// Mounted once (App.js). `user`: the signed-in profile, which names the
// session too.
export const ClarityFields = ({ user }) => {
  useEffect(() => {
    document.addEventListener("change", onChange, true);
    return () => document.removeEventListener("change", onChange, true);
  }, []);
  const email = user && user.email;
  const name = user && user.name;
  useEffect(() => {
    if (email && ![AccountType.OWNER, AccountType.SUPPORT, AccountType.SUPPORTPLUS, AccountType.TRANSLATER].includes(user.role)) {
      clarity("identify", email.toLowerCase(), undefined, undefined, name ? `${name} (${email})` : email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, name]);
  return null;
};
